import axios from '../helpers/axios';
import { logError } from '../services';
import {
  IClientHistory,
  ICompleteBuildPayload,
  IContractDatesValues,
  IUnderContractEmailPayload,
  IPendingContractsRes,
  IResponse,
  IUnderContract,
  IAddressBookPayload,
  IAddressBookData,
  IDropdownResponse,
  IInvitationRes,
  IContractDatesPayload,
  DocumentContractingType,
  IEmailDetails,
  IFileVersion,
  ITenDayCompleteStatusDetails,
  IContractDocumentResults,
  ITodoItem,
  ITestBuild,
  IContractAcceptedNotification,
  IFinalContractNotification,
  IDigitalContractDocument,
  EventType
} from '../models';
import qs from 'querystring';
import { AxiosResponse } from 'axios';

const baseURL = '/api/sales/contracts';
export const getSalesDashboardMetrics = async () => {
  try {
    const { data } = await axios.get(`/api/sales-dashboard/build-metrics`);
    return data;
  } catch (error) {
    logError(error, 'getSalesDashboardMetrics failed');
    return Promise.reject(error);
  }
};

export const getPendingContracts = async (filters?: {
  neighborhoodId?: number | string;
  lotNumber?: number | string;
  contractName?: string;
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  perPage?: number | string;
  statuses?: string[];
}): Promise<IPendingContractsRes> => {
  try {
    const { data } = await axios.get(`${baseURL}/pending-contracts`, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getPendingContracts failed');
    return Promise.reject(error);
  }
};

export const getContractDetails = async (buildId: number | string): Promise<any> => {
  try {
    const { data } = await axios.get(`${baseURL}/${buildId}`);
    return data;
  } catch (error: any) {
    logError(error, 'getContractDetails failed');
    return Promise.reject(error.response.data);
  }
};

export const updateContractPlatted = async (
  buildId: number | string,
  markPlatted: boolean
): Promise<any> => {
  try {
    const { data } = await axios.put(`${baseURL}/platted/${buildId}`, { markPlatted });
    return data;
  } catch (error: any) {
    logError(error, 'updateContractPlatted failed');
    return Promise.reject(error.response.data);
  }
};

export const getApprovedStatusDetails = async (buildId: number | string): Promise<any> => {
  try {
    const { data } = await axios.get(`${baseURL}/approved/${buildId}`);
    return data;
  } catch (error: any) {
    logError(error, 'getApprovedStatusDetails failed');
    return Promise.reject(error.response.data);
  }
};

export const postSalesContractOffer = async (
  buildId: number | string,
  offerData: any
): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/sales/contracts/offer/${buildId}`, offerData);
    return data;
  } catch (error: any) {
    logError({ error, payload: { buildId, offerData } }, 'postSalesContractOffer failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getTenDayCompleteStatusDetails = async (
  buildId: number | string
): Promise<ITenDayCompleteStatusDetails> => {
  try {
    const { data } = await axios.get(`${baseURL}/ten-day-complete/${buildId}`);
    return data;
  } catch (error: any) {
    logError(error, 'getTenDayCompleteStatusDetails failed');
    return Promise.reject(error.response.data);
  }
};

export const postSalesContractTenDayFinalContract = async (
  buildId: number | string,
  finalContract: any
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `${baseURL}/ten-day-complete/${buildId}/final-contract`,
      finalContract
    );
    return data;
  } catch (error) {
    logError(
      { error, payload: { buildId, finalContract } },
      'postSalesContractTenDayFinalContract failed'
    );
    return Promise.reject(error.response.data);
  }
};

// Client Accessible
export const getContractDocuments = async (
  buildId: number | string
): Promise<IContractDocumentResults> => {
  try {
    const { data } = await axios.get(`${baseURL}/documents/${buildId}`);
    return data;
  } catch (error: any) {
    logError(error, 'getContractDocuments failed');
    return Promise.reject(error.response.data);
  }
};

export const getUnderContractDocuments = async (buildId: number | string): Promise<any> => {
  try {
    const { data } = await axios.get(`${baseURL}/under-contract/documents/${buildId}`);
    return data;
  } catch (error: any) {
    logError(error, 'getUnderContractDocuments failed');
    return Promise.reject(error.response.data);
  }
};

export const downloadContractDocument = async (
  buildDocumentId: number | string,
  fileDocumentVersionId?: number | string,
  token?: string | null
): Promise<any> => {
  try {
    const data = await axios.get(`${baseURL}/documents/download/${buildDocumentId}`, {
      responseType: 'blob',
      params: { fileDocumentVersionId, token }
    });
    return data;
  } catch (error: any) {
    logError(error, 'downloadContractDocument failed');
    return Promise.reject(error.response.data);
  }
};

export const downloadContractDocumentPacket = async (buildId: number | string): Promise<any> => {
  try {
    const { data } = await axios.get(`${baseURL}/documents/download-packet/${buildId}`, {
      responseType: 'blob'
    });
    return data;
  } catch (error: any) {
    logError(error, 'downloadContractDocumentPacket failed');
    return Promise.reject(error.response.data);
  }
};

export const generateContractDocuments = async (
  buildId: number | string,
  contractingType: DocumentContractingType
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `${baseURL}/documents/${buildId}/generate?DocumentContractingType=${contractingType}`
    );
    return data;
  } catch (error: any) {
    logError(error, 'generateContractDocuments failed');
    return Promise.reject(error.response.data);
  }
};

export const regenerateContractDocuments = async (buildId: number | string): Promise<any> => {
  try {
    const { data } = await axios.post(`${baseURL}/documents/${buildId}/regenerate`);
    return data;
  } catch (error: any) {
    logError(error, 'regenerateContractDocuments failed');
    return Promise.reject(error.response.data);
  }
};

export const approveContractDocuments = async (buildId: number | string): Promise<any> => {
  try {
    const { data } = await axios.put(`${baseURL}/documents/${buildId}/approve`);
    return data;
  } catch (error: any) {
    logError(error, 'approveContractDocuments failed');
    return Promise.reject(error.response.data);
  }
};

export const completeContractDocuments = async (
  buildId: number | string,
  payload: ICompleteBuildPayload
): Promise<any> => {
  try {
    const { data } = await axios.put(`${baseURL}/ten-day-build/${buildId}/complete`, payload);
    return data;
  } catch (error: any) {
    logError(error, 'completeContractDocuments failed');
    return Promise.reject(error.response.data);
  }
};

export const getUnderContract = async (filters?: {
  territoryId?: number | string;
  neighborhoodIds?: string[];
  jobCode?: number | string;
  lotNumber?: number | string;
  streetAddress?: string;
  clientSearch?: string;
  closingDateLocked?: boolean;
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  perPage?: number;
  showHiddenBuilds?: boolean;
  buildStatuses?: string[];
}): Promise<IResponse<IUnderContract[]>> => {
  const serializeParams = (params: any) => {
    const query = new URLSearchParams();
    for (const key in params) {
      if (Array.isArray(params[key])) {
        params[key].forEach((item: any) => query.append(key, item));
      } else if (params[key] !== undefined) {
        query.append(key, params[key]);
      }
    }
    return query.toString();
  };

  try {
    const queryParams = serializeParams(filters);
    const { data } = await axios.get(`${baseURL}/under-contract?${queryParams}`);
    return data;
  } catch (error) {
    logError(error, 'getUnderContract failed');
    return Promise.reject(error);
  }
};

export const getUnderContractDates = async (
  buildId: number | string
): Promise<IContractDatesValues> => {
  try {
    const { data } = await axios.get(`${baseURL}/under-contract/contract-dates/${buildId}`);
    return data;
  } catch (error) {
    logError(error, 'getUnderContractDates failed');
    return Promise.reject(error);
  }
};

export const updateContractDates = async (
  buildId: number | string,
  payload: IContractDatesPayload
): Promise<any> => {
  try {
    const { data } = await axios.put(
      `${baseURL}/under-contract/contract-dates/${buildId}`,
      payload
    );
    return data;
  } catch (error: any) {
    logError(error, 'updateContractDates failed');
    return Promise.reject(error.response.data);
  }
};

export const getContactHistory = async (
  buildId: string | number,
  filters?: {
    sortBy?: string;
    sortDirection?: string;
    page?: number;
    perPage?: number | string;
  }
): Promise<IResponse<IClientHistory[]>> => {
  try {
    const { data } = await axios.get(`${baseURL}/contact-history/${buildId}`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getContactHistory failed');
    return Promise.reject(error);
  }
};

export const sendUnderContractEmailUpdates = async (
  buildId: number | string,
  payload: IUnderContractEmailPayload
): Promise<any> => {
  try {
    const { data } = await axios.post(`${baseURL}/under-contract/email/${buildId}`, payload);
    return data;
  } catch (error: any) {
    logError(error, 'sendUnderContractEmailUpdates failed');
    return Promise.reject(error.response.data);
  }
};

export const sendUnderContractClosingInvitationEmail = async (
  buildId: number | string,
  payload: IUnderContractEmailPayload
): Promise<any> => {
  try {
    const { data } = await axios.post(`${baseURL}/under-contract/invitation/${buildId}`, payload);
    return data;
  } catch (error: any) {
    logError(error, 'sendUnderContractClosingInvitationEmail failed');
    return Promise.reject(error.response.data);
  }
};

export const updateUnderContractClosingInvitationEmail = async (
  buildId: number | string,
  payload: IUnderContractEmailPayload
): Promise<any> => {
  try {
    const { data } = await axios.put(`${baseURL}/under-contract/invitation/${buildId}`, payload);
    return data;
  } catch (error) {
    logError(error, 'updateUnderContractClosingInvitationEmail failed');
    return Promise.reject(error);
  }
};

export const getUnderContractAddressBook = async (
  buildId: number | string
): Promise<IAddressBookData[]> => {
  try {
    const { data } = await axios.get(`${baseURL}/under-contract/address-book/${buildId}`);
    return data;
  } catch (error) {
    logError(error, 'getUnderContractAddressBook failed');
    return Promise.reject(error);
  }
};

export const updateUnderContractAddressBook = async (
  buildId: number | string,
  payload: IAddressBookPayload
): Promise<any> => {
  try {
    const { data } = await axios.put(`${baseURL}/under-contract/address-book/${buildId}`, payload);
    return data;
  } catch (error) {
    logError(error, 'getUnderContractAddressBook failed');
    return Promise.reject(error);
  }
};

export const getAdditionalContacts = async (
  buildId: number | string
): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(
      `${baseURL}/under-contract/address-book/lookups/additional-contacts/${buildId}`
    );

    return data;
  } catch (error: any) {
    logError(error, 'getAdditionalContacts failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getSuperintendents = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(
      `${baseURL}/under-contract/address-book/lookups/superintendents`
    );

    return data;
  } catch (error: any) {
    logError(error, 'getSuperintendents failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getAddressBookItems = async (buildId: number | string): Promise<any> => {
  try {
    const { data } = await axios.get(`${baseURL}/under-contract/address-book/${buildId}/lookup`);

    return data;
  } catch (error: any) {
    logError(error, 'getAddressBookItems failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getInvitationDetails = async (
  buildId: number | string,
  eventType: string
): Promise<IInvitationRes> => {
  try {
    const { data } = await axios.get(
      `${baseURL}/under-contract/invitation/${buildId}/details/${eventType}`
    );

    return data;
  } catch (error: any) {
    logError(error, 'getInvitationDetails failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getFinalContractNotification = async (
  buildId: number | string
): Promise<IFinalContractNotification> => {
  try {
    const { data } = await axios.get(
      `${baseURL}/ten-day-complete/${buildId}/final-contract/notification`
    );

    return data;
  } catch (error: any) {
    logError(error, 'getFinalContractNotification failed');
    return Promise.reject(error?.response?.data);
  }
};

export const sendFinalContractUploadedEmail = async (
  buildId: number | string,
  payload: IUnderContractEmailPayload
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `${baseURL}/ten-day-complete/${buildId}/final-contract/notification`,
      payload
    );
    return data;
  } catch (error: any) {
    logError(error, 'sendFinalContractUploadedEmail failed');
    return Promise.reject(error.response.data);
  }
};

export const getFinalPriceContractNotification = async (buildId: number | string): Promise<any> => {
  try {
    const { data } = await axios.get(`/api/builds/${buildId}/final-price-contract/notification`);

    return data;
  } catch (error: any) {
    logError(error, 'getFinalPriceContractNotification failed');
    return Promise.reject(error?.response?.data);
  }
};

export const sendFinalPriceContractUploadedEmail = async (
  buildId: number | string,
  payload: IUnderContractEmailPayload
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `/api/builds/${buildId}/final-price-contract/notification`,
      payload
    );
    return data;
  } catch (error: any) {
    logError(error, 'sendFinalPriceContractUploadedEmail failed');
    return Promise.reject(error.response.data);
  }
};

export const replaceFinalContract = async (
  buildId: number | string,
  finalContract: any
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `/api/sales/contracts/under-contract/documents/${buildId}/replace-final-contract`,
      finalContract
    );
    return data;
  } catch (error) {
    logError({ error, payload: { buildId, finalContract } }, 'postSalesContractOffer failed');
    return Promise.reject(error);
  }
};

export const replaceContractDocument = async (
  buildId: number | string,
  buildDocumentTypeId: number | string,
  document: any
): Promise<any> => {
  try {
    const { data } = await axios.put(
      `/api/sales/contracts/under-contract/documents/${buildId}/type/${buildDocumentTypeId}`,
      document
    );
    return data;
  } catch (error: any) {
    logError(
      { error, payload: { buildId, buildDocumentTypeId, document } },
      'replaceContractDocument failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const unapproveContract = async (buildId: string | number): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/sales/contracts/documents/${buildId}/unapprove`);
    return data;
  } catch (error) {
    logError({ error, payload: { buildId } }, 'unapproveContract failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getDigitalContractPackage = async (
  buildId: number | string
): Promise<AxiosResponse<IDigitalContractDocument[]>> => {
  try {
    const data = await axios.get(`${baseURL}/digital/${buildId}/contract-packet`);
    return data;
  } catch (error: any) {
    logError(error, 'getDigitalContractPackage failed');
    return Promise.reject(error);
  }
};

export const getDigitalContractByType = async (
  buildId: number | string,
  buildDocumentType: string
): Promise<any> => {
  try {
    const data = await axios.get(`${baseURL}/digital/${buildId}/contract-document`, {
      params: { buildDocumentType }
    });
    return data;
  } catch (error: any) {
    logError(error, 'getDigitalContractByType failed');
    return Promise.reject(error);
  }
};

export const updateDigitalContract = async (
  buildId: number | string,
  buildDocumentType: string,
  document: any
): Promise<any> => {
  try {
    const { data } = await axios.put(
      `${baseURL}/digital/${buildId}/contract-packet/${buildDocumentType}`,
      document
    );
    return data;
  } catch (error) {
    logError({ error, payload: { buildId, document } }, 'updateDigitalContract failed');
    return Promise.reject(error.response.data);
  }
};

export const createPandaDoc = async (
  buildId: number | string,
  documentType: string
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `${baseURL}/digital/${buildId}/create-panda-doc?DocumentType=${documentType}`
    );
    return data;
  } catch (error) {
    logError({ error, payload: { buildId, documentType } }, 'createPandaDoc failed');
    return Promise.reject(error.response.data);
  }
};

export const createPandaDocForContractPacket = async (buildId: number): Promise<any> => {
  try {
    const { data } = await axios.post(
      `${baseURL}/digital/${buildId}/contract-packet/create-panda-doc`
    );
    return data;
  } catch (error) {
    logError({ error, payload: { buildId } }, 'createPandaDocForContractPacket failed');
    return Promise.reject(error.response.data);
  }
};

export const createPandaDocForDocument = async (
  buildId: number,
  documentType: string
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `${baseURL}/digital/${buildId}/create-panda-doc?DocumentType=${documentType}`
    );
    return data;
  } catch (error) {
    logError({ error, payload: { buildId } }, 'createPandaDocForDocument failed');
    return Promise.reject(error.response.data);
  }
};

export const getEmailPreviewDetails = async (
  contactHistoryId: number | string
): Promise<IEmailDetails> => {
  try {
    const { data } = await axios.get(`${baseURL}/contact-history/${contactHistoryId}/details`);
    return data;
  } catch (error) {
    logError(error, 'getEmailPreviewDetails failed');
    return Promise.reject(error);
  }
};

export const getFileVersions = async (
  buildDocumentId: number | string
): Promise<IFileVersion[]> => {
  try {
    const { data } = await axios.get(`${baseURL}/documents/${buildDocumentId}/versions`);
    return data;
  } catch (error) {
    logError(error, 'getFileVersions failed');
    return Promise.reject(error);
  }
};

export const downloadFileVersion = async (
  buildDocumentId: number | string,
  buildDocumentVersionId: number | string,
  token?: string | null
): Promise<any> => {
  try {
    const data = await axios.get(
      `${baseURL}/documents/download/${buildDocumentId}/versions/${buildDocumentVersionId}`,
      {
        responseType: 'blob',
        params: { token }
      }
    );
    return data;
  } catch (error) {
    logError(error, 'downloadFileVersion failed');
    return Promise.reject(error);
  }
};

export const getOfferContractNotification = async (
  buildId: number | string
): Promise<IContractAcceptedNotification> => {
  try {
    const { data } = await axios.get(`${baseURL}/offer/${buildId}/contract/notification`);

    return data;
  } catch (error: any) {
    logError(error, 'getFinalContractNotification failed');
    return Promise.reject(error?.response?.data);
  }
};

export const sendOfferContractUploadedEmail = async (
  buildId: number | string,
  payload: IUnderContractEmailPayload
): Promise<any> => {
  try {
    const { data } = await axios.post(`${baseURL}/offer/${buildId}/contract/notification`, payload);
    return data;
  } catch (error: any) {
    logError(error, 'sendFinalContractUploadedEmail failed');
    return Promise.reject(error.response.data);
  }
};

export const uploadLenderLetter = async (
  buildId: number | string,
  lenderLetter: any
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `/api/sales/contracts/under-contract/documents/${buildId}/upload-lender-letter`,
      lenderLetter
    );
    return data;
  } catch (error) {
    logError({ error, payload: { buildId, lenderLetter } }, 'uploadLenderLetter failed');
    return Promise.reject(error);
  }
};

export const uploadDeposit = async (buildId: number | string, deposit: any): Promise<any> => {
  try {
    const { data } = await axios.post(
      `/api/sales/contracts/under-contract/documents/${buildId}/upload-deposit`,
      deposit
    );
    return data;
  } catch (error) {
    logError({ error, payload: { buildId, deposit } }, 'uploadDeposit failed');
    return Promise.reject(error?.response?.data);
  }
};

export const cancelCalendarEvent = async (
  buildId: number | string,
  eventType: EventType
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `/api/sales/contracts/under-contract/invitation/${buildId}/cancel`,
      {
        eventType
      }
    );
    return data;
  } catch (error) {
    logError({ error, payload: { buildId } }, 'cancelCalendarInvite failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getTodoItemInfo = async (buildId: number | string): Promise<any> => {
  try {
    const data = await axios.get(`${baseURL}/to-dos/${buildId}`);
    return data;
  } catch (error: any) {
    logError(error, 'getTodoItemInfo failed');
    return Promise.reject(error);
  }
};

export const createTodoItem = async (
  buildId: number | string,
  payload: ITodoItem
): Promise<any> => {
  try {
    const { data } = await axios.post(`${baseURL}/to-dos/${buildId}`, payload);
    return data;
  } catch (error) {
    logError({ error, payload: { buildId, payload } }, 'createTodoItem failed');
    return Promise.reject(error);
  }
};

export const createTodoImages = async (imagePost: FormData): Promise<string[]> => {
  try {
    const { data } = await axios.post(`${baseURL}/to-dos/images`, imagePost);
    return data;
  } catch (error: any) {
    logError(error, 'createTodoImages failed');
    return Promise.reject(error);
  }
};

export const getTestBuilds = async (filters?: {
  neighborhoodId?: number | string;
  lotNumber?: number | string;
  contractName?: string;
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  perPage?: number | string;
}): Promise<IResponse<ITestBuild[]>> => {
  try {
    const { data } = await axios.get(`${baseURL}/test-builds`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getTestBuilds failed');
    return Promise.reject(error);
  }
};
